import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded'
import { useEffect, useState } from 'react'
import { loadScript } from '../globalHeader/common/loadScript'
import { loginSocial, upgradeGuestSocial } from '../globalHeader/functions/ams-functions'
import { analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { reportSharedError } from '../utils/errorReporting'
import { ServerErrors } from '../utils/serverErrors'
import SocialButton from './SocialButton'
import { SocialType } from './SocialLogin'

declare global {
    interface Window {
        FB: any
        fbAsyncInit: any
    }
}

interface IProps {
    facebookAppId: string
    facebookApiVersion: string
    isGuest: boolean
    onSignUpSocial: (socialResponse: any) => void
    onLoginSuccessful: () => void
}

// window.FB outlives this component (remounts must not re-run FB.init)
let fbInitialized = false

const FacebookAuth: React.FC<IProps> = (props) => {
    const { onSignUpSocial, onLoginSuccessful, isGuest, facebookAppId, facebookApiVersion } = props
    const [socialType, setSocialType] = useState(SocialType.FACEBOOK)
    const [unavailable, setUnavailable] = useState(false)

    useEffect(() => {
        const src = 'https://connect.facebook.net/en_US/sdk.js'
        const initFB = () => {
            if (fbInitialized || !window.FB) return
            fbInitialized = true
            window.FB.init({
                appId: facebookAppId,
                cookie: true, // Enable cookies to allow the server to access the session.
                xfbml: true, // Parse social plugins on this webpage.
                version: facebookApiVersion, // Use this Graph API version for this call.
            })
        }

        // sdk.js exits early without calling fbAsyncInit when window.FB already exists (another SDK copy),
        // so also init once the script has loaded.
        window.fbAsyncInit = initFB
        loadScript(src)
            .then(initFB)
            .catch((error) => reportSharedError('Facebook SDK script load failed', error, { src }))

        return () => {
            document.querySelector(`script[src="${src}"]`)?.remove()
        }
    }, [])

    const authCallback = (response: any, data: any) => {
        if (data.error) {
            if (
                data.error.code === ServerErrors.USERNAME_IS_MANDATORY_FOR_FIRST_LOGIN ||
                data.error.code === ServerErrors.SOCIAL_LOGIN_MANDATORY_FIELDS_IS_MISSING
            ) {
                onSignUpSocial({ ...response, socialType })
                // social signup with facebook
            } else {
                console.log('Facebook auth error:', data.error)
            }
        } else {
            analyticsManager.dispatchEvent('completeLogin', {
                authType: 'facebook',
                userId: data.user.id,
                userName: data.user.user_name,
            })
            onLoginSuccessful()
        }
    }

    const handleResponse = () => {
        // window.FB is missing when connect.facebook.net is blocked or has not loaded yet
        setUnavailable(!window.FB)
        if (!window.FB) return
        try {
            window.FB.login(
                (response: any) => {
                    if (response.status === 'connected') {
                        // Logged into your webpage and Facebook.
                        if (isGuest) {
                            upgradeGuestSocial(
                                response.authResponse.accessToken,
                                response.authResponse.signedRequest,
                                socialType,
                            ).then((data) => authCallback(response, data))
                        } else {
                            loginSocial(
                                response.authResponse.accessToken,
                                response.authResponse.signedRequest,
                                socialType,
                            ).then((data) => authCallback(response, data))
                        }
                    } else {
                        // The person is not logged into your webpage or we are unable to tell.
                        console.log('Facebook auth declined')
                    }
                },
                { scope: 'public_profile,email' },
            )
        } catch (error) {
            console.warn('Facebook login failed', error)
            setUnavailable(true)
        }
    }

    return (
        <SocialButton label={unavailable ? 'Unavailable' : 'Facebook'} onClick={handleResponse}>
            <FacebookRoundedIcon
                sx={{
                    width: '23px',
                    height: '23px',
                    fill: '#2374f2',
                }}
            />
        </SocialButton>
    )
}

FacebookAuth.displayName = 'FacebookAuth'

export default FacebookAuth
